// bm-design-system: inline-edit primitive
//
// A field that keeps its own change until it is confirmed. The moment its
// value differs from what the server holds, a tick and a cross appear beneath
// it — the tick commits that one field, the cross puts it back. Nothing else
// on the screen is touched, so there is no whole-form Save button to hunt for
// and no way to lose an edit by leaving one behind.
import * as React from "react";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

export type InlineEditState<T = unknown> = {
  /** The draft — bind the control to this, not to the server value. */
  value: T;
  set: (next: T) => void;
  dirty: boolean;
  saving: boolean;
  error: string | null;
  save: () => void;
  cancel: () => void;
  /** Bump-on-reset, for remounting an uncontrolled control (rich text). */
  version: number;
  /** Enter saves, Escape reverts — on single-line controls. */
  keys: React.KeyboardEventHandler;
  /** The same, for a textarea, where a bare Enter is a newline. */
  multilineKeys: React.KeyboardEventHandler;
};

function sameValue(a: unknown, b: unknown) {
  if (Object.is(a, b)) return true;
  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

function keyHandlers(state: { save: () => void; cancel: () => void }) {
  const save: React.KeyboardEventHandler = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      state.save();
    } else if (e.key === "Escape") {
      e.preventDefault();
      state.cancel();
    }
  };
  const multiline: React.KeyboardEventHandler = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      state.save();
    } else if (e.key === "Escape") {
      e.preventDefault();
      state.cancel();
    }
  };
  return { save, multiline };
}

/** One field, committed on its own. `committed` is what the server holds;
 *  `onSave` is given the draft and resolves when the server has taken it. */
export function useInlineEdit<T>(
  committed: T,
  onSave: (next: T) => Promise<void> | void,
  options: { enabled?: boolean } = {},
): InlineEditState<T> {
  const enabled = options.enabled ?? true;
  const [base, setBase] = React.useState<T>(committed);
  const [draft, setDraft] = React.useState<T>(committed);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [version, setVersion] = React.useState(0);
  // What the server last told us. A sibling field's save reloads the whole
  // page, so props arrive constantly; only a value that genuinely moved
  // (a save landing, a translation, an AI draft) may replace an edit here.
  const seen = React.useRef<T>(committed);

  React.useEffect(() => {
    if (sameValue(seen.current, committed)) return;
    seen.current = committed;
    setBase(committed);
    setDraft(committed);
    setVersion((v) => v + 1);
    setError(null);
  }, [committed]);

  const dirty = enabled && !sameValue(draft, base);

  function save() {
    const next = draft;
    if (saving || sameValue(next, base)) return;
    setSaving(true);
    setError(null);
    Promise.resolve(onSave(next))
      .then(() => {
        seen.current = next;
        setBase(next);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Could not save."))
      .finally(() => setSaving(false));
  }

  function cancel() {
    setDraft(base);
    setVersion((v) => v + 1);
    setError(null);
  }

  const handlers = keyHandlers({ save, cancel });

  return {
    value: draft,
    set: setDraft,
    dirty,
    saving,
    error,
    save,
    cancel,
    version,
    keys: handlers.save,
    multilineKeys: handlers.multiline,
  };
}

/** The tick and the cross. Shows only once the field has an uncommitted
 *  change (or a failure to report), so a screen at rest stays quiet. */
export function InlineEditBar({
  state,
  className,
}: {
  state: Pick<InlineEditState, "dirty" | "saving" | "error" | "save" | "cancel">;
  className?: string;
}) {
  if (!state.dirty && !state.saving && !state.error) return null;

  return (
    <div className={cn("flex flex-wrap items-center gap-2 pt-1", className)}>
      <button
        type="button"
        onClick={state.save}
        disabled={state.saving || !state.dirty}
        aria-label="Save this change"
        title="Save this change (Enter)"
        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-success bg-success-faded text-success-display transition-colors hover:bg-success hover:text-white disabled:opacity-40"
      >
        <Check className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={state.cancel}
        disabled={state.saving}
        aria-label="Discard this change"
        title="Discard this change (Esc)"
        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-hairline text-ink-muted transition-colors hover:border-danger hover:bg-danger-faded hover:text-danger-display disabled:opacity-40"
      >
        <X className="h-4 w-4" />
      </button>
      <span className="text-xs text-ink-muted">
        {state.saving ? "Saving…" : state.error ? "Not saved" : "Unsaved change"}
      </span>
      {state.error && <span className="text-xs text-danger-display">{state.error}</span>}
    </div>
  );
}

/** Label, control, hint, tick-and-cross — the shape every editable field on
 *  an admin screen takes. `above` is for the English-original line the
 *  Spanish views show over their input. */
export function InlineField({
  label,
  htmlFor,
  hint,
  error,
  state,
  above,
  children,
  className,
}: {
  label?: React.ReactNode;
  htmlFor?: string;
  hint?: React.ReactNode;
  /** A server-side error for this field from a whole-form submit. */
  error?: string;
  state: Pick<InlineEditState, "dirty" | "saving" | "error" | "save" | "cancel">;
  above?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    // data-inline-field names the block a tick belongs to: some controls wrap
    // themselves in a div of their own, so "the field around this input" is
    // not reliably its parent.
    <div className={cn("space-y-1.5", className)} data-inline-field={htmlFor}>
      {label && <label htmlFor={htmlFor}>{label}</label>}
      {above}
      {children}
      {hint && <p className="text-xs text-ink-muted">{hint}</p>}
      {error && <p className="text-xs text-danger-display">{error}</p>}
      <InlineEditBar state={state} />
    </div>
  );
}
